import {
  useQueryState,
  removeEmptyQueryParams,
  useDashboardStore,
  MetricFilterState,
  ISelect,
  RepoDto,
  MetricTooltip,
} from '@devd-client/devd/components';
import { OptionBase } from 'chakra-react-select';
import { useState, useEffect } from 'react';
import {
  useContributorTableMetrics,
  useFetchJobRolesList,
  useFetchLanguages,
  useFetchRepo,
  useGetContributorTableMetricsForTeams,
  useTeammenu,
} from '../api';
import { useLocation } from 'react-router-dom';
export interface Option extends OptionBase {
  label: string;
  value: string;
}

interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
}
export const useContributorTable = () => {
  const { selected, selectedTeam } = useDashboardStore();
  const { pathname } = useLocation();

  const { data: repoList, isLoading: repoLoading } = useFetchRepo('git');
  const { data: teamList, isLoading: teamLoading } = useTeammenu();

  const { data: languagesList, isLoading: languagesLoading } =
    useFetchLanguages();
  const { data: metricList } = useContributorTableMetrics();
  const { data: contributorTableMetricsForTeams } =
    useGetContributorTableMetricsForTeams(
      decodeURIComponent(pathname.split('/')[2])
    );
  const { data: jobRoleList, isLoading: jobRoleLoading } =
    useFetchJobRolesList();

  const [search, setSearch] = useQueryState('search');
  const [repo, setRepo] = useQueryState('repo');
  const [language, setLanguage] = useQueryState('language');
  const [jobRole, setJobRole] = useQueryState('jobRole');
  const [orderByUrl, setOrderByUrl] = useQueryState('orderBy');
  const [orderColumnUrl, setOrderColumnUrl] = useQueryState('orderColumn');
  const [searchTerm, setSearchTerm] = useState('');
  const [compareToUrl, setCompareToUrl] = useQueryState('compareTo');
  const [showContributorPercentiles, setShowContributorPercentiles] =
    useQueryState('showContributorPercentiles');

  const [repoState, setRepoState] = useState<ISelect[]>([]);
  const [metricFilterState, setMetricFilterState] = useState<
    MetricFilterState[]
  >([]);
  const [languageState, setLanguageState] = useState<ISelect[]>([]);
  const [jobRoleState, setJobRoleState] = useState<ISelect[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<MetricState[]>([]);
  const [selectedPermanentColumns, setSelectedPermanentColumns] = useState<
    string[]
  >([]);
  const [showContributorPercentilesState, setShowContributorPercentilesState] =
    useState(false);
  const [orderBy, setOrderBy] = useState<string>();
  const [orderColumn, setOrderColumn] = useState<string>();
  const [compareTo, setCompareTo] = useState<'pastPeriod' | ''>('');

  useEffect(() => {
    if (typeof searchTerm !== 'undefined') {
      searchTerm === ''
        ? removeEmptyQueryParams({ search: '' })
        : setSearch(searchTerm);
    }
    if (repoState && repoState?.length < 1) {
      removeEmptyQueryParams({ repo: '' });
    } else {
      setRepo(repoState?.map((repo: ISelect) => repo.label).join(', '));
    }
    if (languageState && languageState?.length < 1) {
      removeEmptyQueryParams({ language: '' });
    } else {
      setLanguage(languageState?.map((item: ISelect) => item.value).join(', '));
    }
    if (jobRoleState && jobRoleState?.length < 1) {
      removeEmptyQueryParams({ jobRole: '' });
    } else {
      setJobRole(jobRoleState?.map((item: ISelect) => item.value).join(', '));
    }
    if (typeof orderBy !== 'undefined') {
      orderBy === ''
        ? removeEmptyQueryParams({ orderBy: '' })
        : setOrderByUrl(orderBy);
    }
    if (typeof orderColumn !== 'undefined') {
      orderColumn === ''
        ? removeEmptyQueryParams({ orderColumn: '' })
        : setOrderColumnUrl(orderColumn);
    }

    if (typeof compareTo !== 'undefined') {
      compareTo === ''
        ? removeEmptyQueryParams({ compareTo: '' })
        : setCompareToUrl(compareTo);
    }
    if (typeof showContributorPercentilesState !== 'undefined') {
      showContributorPercentilesState
        ? setShowContributorPercentiles(true)
        : removeEmptyQueryParams({ showContributorPercentiles: '' });
    }
  }, [
    selected,
    selectedTeam.value,
    searchTerm,
    repoState,
    languageState,
    selectedMetrics,
    orderBy,
    orderColumn,
    compareTo,
    showContributorPercentilesState,
    jobRoleState,
  ]);

  useEffect(() => {
    search && setSearchTerm(search);
    language &&
      setLanguageState(
        language
          ?.split(', ')
          .map((item: string) => ({ value: item, label: item }))
      );
    jobRole &&
      setJobRoleState(
        jobRole
          ?.split(', ')
          .map((item: string) => ({ value: item, label: item }))
      );
    orderByUrl && setOrderBy(orderByUrl);
    orderColumnUrl && setOrderColumn(orderColumnUrl);
    compareToUrl && setCompareTo(compareToUrl as 'pastPeriod' | '');
    showContributorPercentiles && setShowContributorPercentilesState(true);
  }, []);
  useEffect(() => {
    if (!repoList?.dto) return;
    repo &&
      setRepoState(
        repoList?.dto
          ?.filter((el: RepoDto) => repo?.split(', ')?.includes(el.name))
          ?.map((item: RepoDto) => ({ value: item.key, label: item.name }))
      );

    if (!metricList?.dto) return;

    if (
      contributorTableMetricsForTeams?.dto &&
      contributorTableMetricsForTeams?.dto?.length > 0
    ) {
      setSelectedMetrics(
        contributorTableMetricsForTeams?.dto
          ?.flatMap((metric: string) => {
            if (metric === 'GOALS' || metric === 'KEY_RESULTS') {
              return {
                title: metric === 'GOALS' ? 'Goals' : 'Key Results',
                metric,
              };
            }
            return metricList?.dto.filter(
              (el: MetricTooltip) => el.metric === metric
            );
          })
          .map((item: any, index: number) => ({
            metricTitle: item?.title ?? '',
            metricKey: item?.metric,
            sequence: index,
          }))
      );
      const permanentColumns = contributorTableMetricsForTeams?.dto?.filter(
        (item: string) =>
          ['Teams', 'Languages', 'Knowledge Area', 'Team Allocation'].includes(
            item
          )
      );
      setSelectedPermanentColumns(['Contributors', ...permanentColumns]);
    } else {
      setSelectedPermanentColumns(['Contributors']);
      contributorTableMetricsForTeams?.dto &&
        setSelectedMetrics(
          metricList?.dto
            .filter((el: MetricTooltip) =>
              [
                'WORK_BREAKDOWN_CODE_IMPACT',
                'STORY_POINT_COMPLETED',
                'PR_OPEN',
                'PR_CYCLE_TIME',
                'PR_REVIEWED',
                'ACTIVE_DAYS',
              ].includes(el.metric)
            )
            .map((item: any, index: number) => ({
              metricTitle: item?.title ?? '',
              metricKey: item?.metric,
              sequence: index,
            }))
        );
    }
  }, [repoList?.dto, metricList?.dto, contributorTableMetricsForTeams?.dto]);
  return {
    searchTerm,
    setSearchTerm,
    repoState,
    setRepoState,
    repoList,
    languageState,
    setLanguageState,
    languagesList,
    selectedMetrics,
    setSelectedMetrics,
    selectedPermanentColumns,
    setSelectedPermanentColumns,
    orderBy,
    setOrderBy,
    orderColumn,
    setOrderColumn,
    compareTo,
    setCompareTo,
    teamList,
    metricFilterState,
    setMetricFilterState,
    showContributorPercentilesState,
    setShowContributorPercentilesState,
    jobRoleState,
    setJobRoleState,
    jobRoleList,
  };
};
